/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getBranches, getDoctors } from '../../api';
import {
    adaptBranchesToSelector,
    adaptDoctorsToSelector,
    adaptSpecializationsToSelector,
    filterDoctorsBySelectedBranch,
    filterDoctorsForChild,
    filterDoctorsBySelectedSpecialization,
    findDoctorById,
    getSpecializationsFromDoctors,
} from './utils';
import { formatFullDate } from '../../utils';

const defaultPickerStartDate = formatFullDate(new Date());

// eslint-disable-next-line consistent-return
// eslint-disable-next-line max-len
export const fetchBranches = createAsyncThunk(
    'filters/fetchBranches',
    async ({
        baseUrl,
        currentBranchId,
    }, { rejectWithValue }) => {
        try {
            const response = await (await getBranches())(baseUrl);
            // eslint-disable-next-line no-nested-ternary
            return currentBranchId ? typeof currentBranchId === 'string' ? response.data.filter(e => e.branchId === currentBranchId) : response.data.filter(e => currentBranchId?.some(id => id === e.branchId)) : response.data;// ?.slice(0, 1);
        } catch (err) {
            rejectWithValue(err);
            throw err;
        }
    },
);
// eslint-disable-next-line consistent-return
// eslint-disable-next-line max-len
export const fetchDoctors = createAsyncThunk('filters/fetchDoctors', async ({
    baseUrl,
    currentBranchId,
}, { rejectWithValue }) => {
    try {
        const response = await (await getDoctors())(baseUrl);
        return currentBranchId ? typeof currentBranchId === 'string' ? response.data.filter(e => e.branches.some(b => b === currentBranchId)) : response.data.filter(e => e.branches.some(b => currentBranchId?.some(id => id === b))) : response.data;
    } catch (err) {
        rejectWithValue(err);
        throw err;
    }
});

export const initState = {
    branches: [],
    branchesMetadata: [],
    doctors: [],
    specializations: [],
    doctorsMetadata: [],
    specializationsMetadata: [],
    selectedBranch: undefined,
    selectedDoctor: undefined,
    selectedSpecialization: undefined,
    forChild: false,
    childrenAge: -1,
    isLoading: false,
    isBranchesLoading: true,
    isDoctorsLoading: true,
    error: undefined,
    date: defaultPickerStartDate,
};

const updateDoctorFilters = (state) => {
    let doctors = filterDoctorsForChild(state.doctors, state.forChild, state.childrenAge);
    if (state.selectedBranch) {
        doctors = filterDoctorsBySelectedBranch(doctors, state.selectedBranch);
    }
    const specializations = getSpecializationsFromDoctors(doctors);
    state.specializationsMetadata = adaptSpecializationsToSelector(specializations);
    if (state.selectedSpecialization && !specializations.some(
        doctor => `${doctor.specialityId}` === `${state.selectedSpecialization}`,
    )) {
        state.selectedSpecialization = undefined;
    }
    if (state.selectedSpecialization) {
        doctors = filterDoctorsBySelectedSpecialization(doctors, state.selectedSpecialization);
    }
    state.doctorsMetadata = adaptDoctorsToSelector(doctors);
    if (state.selectedDoctor && !findDoctorById(doctors, state.selectedDoctor)) {
        state.selectedDoctor = undefined;
    }
};

export const filtersSlice = createSlice({
    name: 'filters',
    initialState: { ...initState },
    reducers: {
        selectBranch: (state, action) => {
            state.selectedBranch = action.payload;
            updateDoctorFilters(state);
        },
        selectBranchSimplified: (state, action) => {
            state.selectedBranch = action.payload;
            updateDoctorFilters(state);
        },
        selectSpecialization: (state, action) => {
            state.selectedSpecialization = action.payload;
            state.selectedDoctor = undefined;
            updateDoctorFilters(state);
        },
        selectChildrenFlag: (state, action) => {
            state.forChild = !!action.payload.forChild;
            state.childrenAge = Number(state.forChild ? action.payload.childrenAge ?? -1 : -1);
            updateDoctorFilters(state);
        },
        selectDoctor: (state, action) => {
            const selectedDoctorId = action.payload;
            let targetDoctor = {};
            let { selectedSpecialization } = state;
            if (selectedDoctorId) {
                targetDoctor = findDoctorById(state.doctors, selectedDoctorId);
                selectedSpecialization = targetDoctor && targetDoctor.specialityId;
            }
            state.selectedDoctor = selectedDoctorId;
            state.selectedSpecialization = selectedSpecialization;
            updateDoctorFilters(state);
        },
        selectDate: (state, action) => {
            state.date = action.payload;
        },
        resetSelectedFilters: (state) => {
            state.selectedBranch = undefined;
            state.selectedDoctor = undefined;
            state.selectedSpecialization = undefined;
            state.forChild = false;
            state.childrenAge = -1;
            state.date = defaultPickerStartDate;
            updateDoctorFilters(state);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBranches.pending, (state) => {
                state.isBranchesLoading = true;
                state.error = undefined;
            })
            .addCase(fetchBranches.fulfilled, (state, action) => {
                state.branches = [...action.payload];
                state.branchesMetadata = adaptBranchesToSelector(action.payload);
                state.isBranchesLoading = false;
                state.error = undefined;
                // if (action.payload.length === 1) {
                //      state.selectedBranch = action.payload[0].branchId;
                // }
            })
            .addCase(fetchBranches.rejected, (state, { error }) => {
                state.branches = [];
                state.branchesMetadata = [];
                state.isBranchesLoading = false;
                state.error = error.message;
            })
            .addCase(fetchDoctors.pending, (state) => {
                state.isDoctorsLoading = true;
                state.error = undefined;
            })
            .addCase(fetchDoctors.fulfilled, (state, action) => {
                let doctors = [...action.payload];
                const {
                    selectedSpecialization,
                    selectedBranch,
                } = state;
                if (selectedBranch) {
                    doctors = filterDoctorsBySelectedBranch(doctors, selectedBranch);
                }
                const specializations = getSpecializationsFromDoctors(doctors);
                if (selectedSpecialization) {
                    doctors = filterDoctorsBySelectedSpecialization(doctors, selectedSpecialization);
                }
                state.doctors = [...action.payload];
                state.doctorsMetadata = adaptDoctorsToSelector(doctors);
                state.specializations = specializations;
                state.specializationsMetadata = adaptSpecializationsToSelector(specializations);
                updateDoctorFilters(state);
                state.isDoctorsLoading = false;
                state.error = undefined;
            })
            .addCase(fetchDoctors.rejected, (state, { error }) => {
                state.doctors = [];
                state.doctorsMetadata = [];
                state.specializations = [];
                state.specializationsMetadata = [];
                state.isDoctorsLoading = false;
                state.error = error.message;
            });
    },
});

export const {
    selectBranch,
    selectDoctor,
    selectSpecialization,
    selectDate,
    selectChildrenFlag,
    selectBranchSimplified,
    resetSelectedFilters,
} = filtersSlice.actions;

export default filtersSlice.reducer;
